import { create } from "zustand";
import api, { apiFormData, isAbortError } from "../../utils/api";
import { getError } from "../../utils/getError";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";

export interface Driver {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  license_id: string;
  license_exp: { _seconds: number; _nanoseconds: number };
  license_expedition: { _seconds: number; _nanoseconds: number };
  driverStatus: string;
  profileImg: string;
  vehicle_id: string | null;
  remolque_id: string | null;
  /** @deprecated drivers viejos en Firestore usan camelCase. Leer con fallback. */
  vehicleId?: string | null;
  /** @deprecated drivers viejos en Firestore usan camelCase. Leer con fallback. */
  remolqueId?: string | null;
  updatedAt: { _seconds: number; _nanoseconds: number };
  hours: number;
  days: number;
  companyId: string | null;
  collections: string[];
  contactsEmergency: {
    name: string;
    phone: string;
    email: string;
  }[];
}

export interface CreateDriverData {
  userId: string;
  firstName: string;
  lastName: string;
  licenseId: string;
  licenseExp: string;
  driverStatus: string;
  contactsEmergency: {
    name: string;
    phone: string;
    email: string;
  }[];
  profileImg?: string;
  img_url?: string;
}

export interface AvailableUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  username?: string;
  profileImg?: string;
}

export interface AvailableDriver {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profileImg?: string;
  /** Vencimiento de la licencia: /availableDrivers devuelve el documento completo del conductor. */
  licenseExp?: string | { _seconds: number; _nanoseconds?: number } | null;
}

export interface ChangeDriverParams {
  newDriverId: string;
  oldDriverId?: string;
  vehicleId?: string;
  remolqueId?: string;
  tripId?: string;
  lang?: string;
}

interface DriversStore {
  drivers: Driver[];
  availableUsers: AvailableUser[];
  availableDrivers: AvailableDriver[];
  loading: boolean;
  error: string | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  fetchDrivers: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
    status?: string;
    search?: string;
  }) => Promise<void>;
  fetchAvailableUsers: (currentDriverId?: string) => Promise<void>;
  getDriverById: (
    id: string,
  ) => Promise<{ success: boolean; data?: Driver; error?: string }>;
  createDriver: (
    driverData: CreateDriverData | FormData,
  ) => Promise<{ success: boolean; data?: Driver; error?: string }>;
  updateDriver: (
    id: string,
    driverData: CreateDriverData | FormData,
  ) => Promise<{ success: boolean; data?: Driver; error?: string }>;
  deleteDriver: (
    id: string,
  ) => Promise<{ success: boolean; error?: string }>;
  fetchAvailableDrivers: () => Promise<void>;
  getDriverByVehicleId: (
    vehicleId: string,
  ) => Promise<{ success: boolean; data?: Driver; error?: string }>;
  changeDriver: (
    params: ChangeDriverParams,
  ) => Promise<{ success: boolean; data?: any; error?: string; status?: number }>;
}

export const useDriversStore = create<DriversStore>((set, get) => ({
  drivers: [],
  availableUsers: [],
  availableDrivers: [],
  loading: false,
  error: null,
  meta: undefined,

  fetchDrivers: async (params = {}) => {
    if (get().loading) return;
    const fetchGen = getAdminFetchGeneration();

    set({ loading: true, error: null });
    try {
      const { data } = await api.get("/driverList", {
        params: {
          limit: params.limit ?? 6,
          page: params.page ?? 1,
          ...params,
        },
      });
      if (getAdminFetchGeneration() !== fetchGen) return;
      set({ drivers: data.data || [], meta: data.meta, loading: false });
    } catch (err: any) {
      if (getAdminFetchGeneration() !== fetchGen || isAbortError(err)) return;
      set({ error: getError(err), loading: false });
    }
  },

  fetchAvailableUsers: async (currentDriverId?: string) => {
    try {
      const params = currentDriverId ? { currentDriverId } : {};
      const { data } = await api.get("/availableUsers", { params });
      const usersPayload = data?.data ?? data;
      const users = Array.isArray(usersPayload)
        ? usersPayload
        : Array.isArray(usersPayload?.data)
          ? usersPayload.data
          : [];
      // Mapear campos de snake_case a camelCase
      const mappedUsers = users.map((user: any) => ({
        id: user.id,
        firstName: user.first_name || user.firstName || "",
        lastName: user.last_name || user.lastName || "",
        email: user.email || "",
        username: user.username || "",
        profileImg: user.profileImg || user.profile_img || "",
      }));
      set({ availableUsers: mappedUsers });
    } catch (err: any) {
      console.error("Error fetching available users:", err);
    }
  },

  fetchAvailableDrivers: async () => {
    try {
      const { data } = await api.get("/availableDrivers");
      const driversPayload = data?.data ?? data;
      const drivers = Array.isArray(driversPayload)
        ? driversPayload
        : Array.isArray(driversPayload?.data)
          ? driversPayload.data
          : [];
      set({ availableDrivers: drivers });
    } catch (err: any) {
      console.error("Error fetching available drivers:", err);
    }
  },

  getDriverById: async (id: string) => {
    try {
      const { data } = await api.get("/driver", { params: { id } });
      const driver = data?.data?.data ?? data;
      return { success: true, data: driver };
    } catch (err: any) {
      console.error("Error fetching driver by ID:", err);
      const message =
        err.response?.data?.message || err.message || "Unknown error";
      return { success: false, error: message };
    }
  },

  getDriverByVehicleId: async (vehicleId: string) => {
    try {
      const { data } = await api.get("/driverList", {
        params: { vehicle_id: vehicleId, limit: 1, page: 1 },
      });
      const list: Driver[] = data?.data ?? data?.drivers ?? [];
      const driver = Array.isArray(list)
        ? (list.find((d) => d.vehicleId === vehicleId) ?? list[0])
        : undefined;
      if (driver) return { success: true, data: driver };
      return { success: false, error: "No driver found for this vehicle" };
    } catch (err: any) {
      console.error("Error fetching driver by vehicleId:", err);
      return { success: false, error: err.message || "Unknown error" };
    }
  },

  createDriver: async (driverData: CreateDriverData | FormData) => {
    try {
      const isFormData = driverData instanceof FormData;

      const apiInstance = isFormData ? apiFormData : api;

      const res = await apiInstance.post("/driver", driverData, {
        params: isFormData ? undefined : undefined,
      });

      const newDriver = res.data?.data ?? res.data;
      if (newDriver) {
        set((state) => ({
          drivers: [...state.drivers, newDriver],
        }));
      }

      return { success: true, data: newDriver };
    } catch (err: any) {
      console.error("Error creating driver:", err);
      const message =
        err.response?.data?.message || err.message || "Unknown error";
      return { success: false, error: message };
    }
  },

  updateDriver: async (id: string, driverData: CreateDriverData | FormData) => {
    try {
      const isFormData = driverData instanceof FormData;

      const apiInstance = isFormData ? apiFormData : api;

      const res = await apiInstance.put("/driver", driverData, {
        params: { id },
      });

      const updatedDriver = res.data?.data ?? res.data;
      if (updatedDriver) {
        set((state) => ({
          drivers: state.drivers.map((driver) =>
            driver.id === updatedDriver.id ? updatedDriver : driver,
          ),
        }));
      }

      return { success: true, data: updatedDriver };
    } catch (err: any) {
      console.error("Error updating driver:", err);
      const message =
        err.response?.data?.message || err.message || "Unknown error";
      return { success: false, error: message };
    }
  },

  deleteDriver: async (id: string) => {
    try {
      await api.delete("/driver", { params: { id } });
      set((state) => ({
        drivers: state.drivers.filter((driver) => driver.id !== id),
      }));
      return { success: true };
    } catch (err: any) {
      console.error("Error deleting driver:", err);
      const message =
        err.response?.data?.message || err.message || "Unknown error";
      return { success: false, error: message };
    }
  },

  changeDriver: async (params: ChangeDriverParams) => {
    try {
      const { lang, ...bodyParams } = params;
      const res = await api.post("/changeDriver", bodyParams, {
        params: lang ? { lang } : undefined,
        headers: { "Content-Type": "application/json" },
      });

      const responseData = res.data?.data ?? res.data;
      return { success: true, data: responseData };
    } catch (err: any) {
      console.error("Error changing driver:", err);
      const message =
        err.response?.data?.message || err.message || "Unknown error";
      return { success: false, error: message, status: err.response?.status };
    }
  },
}));
